import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Spin, Select } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

export default function GenderSummary() {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
  const [organisations, setOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Organisations
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: {
          Authorization: `Bearer ${authData?.access}`,
        },
      });
      setOrganisations(res.data.results || []);
    } catch (error) {
      console.error("Error fetching organisations:", error);
    }
  };

  // Fetch Gender Summary
  const fetchGenderSummary = async (orgId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${backUrl}/api/reports/organisation-gender-summary/?organisation_id=${orgId}`
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching gender summary:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    if (organisationId) fetchGenderSummary(organisationId);
  }, [organisationId, backUrl]);

  const COLORS = ["#3B82F6", "#EC4899"]; // Blue = Male, Pink = Female

  const chartData =
    data && data.total_employees > 0
      ? [
          { name: "Males", value: data.total_males },
          { name: "Females", value: data.total_females },
        ]
      : [
          { name: "Males", value: 0 },
          { name: "Females", value: 0 },
        ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex flex-col justify-start items-center">
      {/* Organisation Filter */}
      <div className="flex gap-4 mb-6">
        <Select
          value={organisationId}
          onChange={(value) => setOrganisationId(value)}
          style={{ width: 280 }}
          placeholder="Select Organisation"
        >
          {organisations.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.name}
            </Option>
          ))}
        </Select>
      </div>

      {/* Chart Section */}
      {loading ? (
        <Spin size="large" />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card
            title="Gender Distribution"
            bordered={false}
            className="shadow-md rounded-2xl w-[420px]"
            style={{marginTop:10}}
          >
            <div style={{ width: "100%", height: 340 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} employees`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="text-center mt-4 text-gray-700">
              <p className="font-semibold">
                Total Employees: {data?.total_employees || 0}
              </p>
              
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
